import {Component} from '@angular/core';
import {NavController, Platform} from '@ionic/angular';
import {StorageManagerGatewayCapacitor} from './adapters/real/app-services/storage-manager-gateway-capacitor';
import {SocketIoService} from './common/services/socket-io.service';
import {RouterStateServiceService} from './common/services/router-state-service.service';
import {GetUserUseCase} from './core-logic/uses-cases/user/get-user-use-case';
import {UserModel} from './core-logic/models/user/user-model';
import {BusinessLogoManagerService} from './common/services/business-logo-manager.service';
import {StorageBusinessesManagerService} from './common/services/storage-businesses-manager.service';
import {StatusBar, Style} from '@capacitor/status-bar';
import {SplashScreen} from '@capacitor/splash-screen';
import {Device, DeviceInfo} from '@capacitor/device';
import {App, AppInfo} from '@capacitor/app';
import * as _ from 'lodash';
import {FcmManagerService} from './common/services/fcm-manager.service';
import {ModalService} from './common/services/modal.service';
import {GetAppVersionUseCase} from './core-logic/uses-cases/configuration/get-app-version-use-case';
import {CustomModalModel, ModalMessageType} from './core-logic/models/custom-modal/custom-modal-model';
import {NotificationManagerService} from './common/services/notification-manager.service';
import {StorageLoyaltiesCardsManagerService} from './common/services/storage-loyalties-cards-manager.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent {
  constructor(
    private platform: Platform,
    private navCtrl: NavController,
    private getUserUseCase: GetUserUseCase,
    private socketIoService: SocketIoService,
    private fcmManagerService: FcmManagerService,
    private customModalService: ModalService,
    private getAppVersionUseCase: GetAppVersionUseCase,
    private routerStateServiceService: RouterStateServiceService,
    private businessLogoManagerService: BusinessLogoManagerService,
    private notificationManagerService: NotificationManagerService,
    private storageManagerGatewayCapacitor: StorageManagerGatewayCapacitor,
    private storageBusinessesManagerService: StorageBusinessesManagerService,
    private storageLoyaltiesCardsManagerService: StorageLoyaltiesCardsManagerService,
  ) {
    this.initializeApp();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.routerStateServiceService.init();
      this.fcmManagerService.init();
      this.storageManagerGatewayCapacitor.getCurrentUser().subscribe(currentUser => {
        if (currentUser) {
          currentUser = this.refreshStoredUser(currentUser);
          this.initServices();
          this.navigateToContextualLocation(currentUser);
          this.checkUpdateAvailability();
        }
      });

      Device.getInfo().then(deviceInfo => {
        if ( !_.isEqual(deviceInfo.platform, 'web')) {
          StatusBar.setStyle( {style: Style.Default});
          SplashScreen.hide();
        }
      });
    });
  }

  private checkUpdateAvailability() {
    Promise.all([Device.getInfo(), App.getInfo()])
      .then(deviceInfoAndAppInfo => {
        const deviceInfo: DeviceInfo = deviceInfoAndAppInfo[0];
        const appInfo: AppInfo = deviceInfoAndAppInfo[1];
        if (!_.isEqual(deviceInfo.platform, 'web')) {
          this.getAppVersionUseCase.execute(null).subscribe(appVersion => {
            if (_.isString(appVersion) && !_.isEqual(appInfo.version, appVersion)) {
              const customModalModel: CustomModalModel = this.makeAppUpgradeCustomModal();
              this.customModalService.createCustomModal(customModalModel).subscribe();
            }
          });
        }
      }).catch(() => {})
  }

  private makeAppUpgradeCustomModal() {
    return {
      title: 'Mise à jour disponible',
      message: {
        value:
          '<div class="ion-text-center">' +
          '    <img src="assets/icons/fydl-coral.png" alt="Fydl icon" style="width: 10em">' +
          '  </div> Une nouvelle version de l\'application est disponible &#127882; . ' +
          '<br><br> &#128073; <a href="https://fydl-app.com/download-app"> Clique ici pour l\'obtenir</a>',
        type: ModalMessageType.HTML
      },
      customizer: {color: 'coral'},
      disable_close: true
    };
  }

  private initServices() {
    this.socketIoService.initConnection();
    this.businessLogoManagerService.init();
    this.storageBusinessesManagerService.init();
    this.storageLoyaltiesCardsManagerService.init();
    this.notificationManagerService.init();
  }

  private refreshStoredUser(currentUser: UserModel) {
    this.getUserUseCase.execute(null).subscribe(user => {
      currentUser = user as UserModel;
      this.storageManagerGatewayCapacitor.setCurrentUser(currentUser).subscribe();
    });
    return currentUser;
  }

  private navigateToContextualLocation(currentUser: UserModel) {
    if (currentUser.loyalty_cards.length === 0 && currentUser.businesses.length === 0) {
      this.navCtrl.navigateRoot('/registration/role-choice');
    } else if (currentUser.loyalty_cards.length === 0 && currentUser.businesses.length > 0) {
      this.storageManagerGatewayCapacitor.setBusinessViewMode().subscribe(() => this.navCtrl.navigateRoot('/business'));
    } else {
      this.storageManagerGatewayCapacitor.getBusinessViewMode().subscribe(isItBusinessView => {
          if (isItBusinessView) {
            currentUser.businesses.length > 0 ? this.navCtrl.navigateRoot('/business') :
              this.navCtrl.navigateRoot('/business/create-business');
          } else {
            this.storageManagerGatewayCapacitor.unsetBusinessViewMode().subscribe(() => this.navCtrl.navigateRoot('/customer'));
          }
      })
    }
  }
}
